import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Modal } from '@sw-sw/lib-ui';
import { toast } from 'react-toastify';
import authorizationAgreementApi, {
  Agreement,
  AgreementSignability,
} from '../../../utils/api/authorizationAgreement';
import AppContext from '../../../contexts/AppContext';

interface Props {
  show: boolean;
  onClose: () => void;
  projectId: number;
  agreement: Agreement | null;
  signability: AgreementSignability;
  onSigned: () => void;
  clientName?: string | null;
}

const labelStyle: React.CSSProperties = { color: '#666', fontSize: 12 };
const valueStyle: React.CSSProperties = { fontWeight: 'bold' };
const readOnlyFieldStyle: React.CSSProperties = {
  background: '#f5f5f5',
  border: '1px solid #ddd',
  borderRadius: 3,
  padding: '6px 8px',
  fontSize: 13,
  color: '#333',
};

const SignatureBlock = ({
  role,
  name,
  title,
  signedAt,
  canSign,
  currentUserName,
  currentUserTitle,
  onSign,
  disabled,
}: {
  role: string;
  name: string | null;
  title: string | null;
  signedAt: string | null;
  canSign: boolean;
  currentUserName: string;
  currentUserTitle: string;
  onSign: () => void;
  disabled: boolean;
}) => {
  if (signedAt) {
    return (
      <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: 4, padding: 16 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
          {role} Signature
        </h4>
        <div style={{ marginBottom: 4 }}>
          <span style={labelStyle}>Name: </span>
          <strong style={valueStyle}>{name}</strong>
        </div>
        <div style={{ marginBottom: 4 }}>
          <span style={labelStyle}>Title: </span>
          <strong style={valueStyle}>{title}</strong>
        </div>
        <div>
          <span style={labelStyle}>Date: </span>
          <strong style={valueStyle}>
            {new Date(signedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </strong>
        </div>
      </div>
    );
  }

  if (!canSign) {
    return (
      <div style={{ flex: 1, border: '1px solid #ccc', borderRadius: 4, padding: 16, backgroundColor: '#f9f9f9' }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
          {role} Signature
        </h4>
        <p style={{ color: '#888', fontStyle: 'italic', margin: 0, fontSize: 13 }}>
          Awaiting {role.toLowerCase()} signature
        </p>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, border: '1px solid #4a90e2', borderRadius: 4, padding: 16 }}>
      <h4 style={{ margin: '0 0 12px', fontSize: 13, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
        {role} Signature
      </h4>
      <div style={{ marginBottom: 10 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 4 }}>
          Full Name
        </label>
        <div style={readOnlyFieldStyle}>{currentUserName || '—'}</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 4 }}>
          Title / Position
        </label>
        <div style={readOnlyFieldStyle}>{currentUserTitle || '—'}</div>
      </div>
      <button
        className='primary small'
        disabled={disabled}
        onClick={onSign}
      >
        Sign
      </button>
    </div>
  );
};

const PROJECT_TYPE_OPTIONS = [
  'Land Development',
  'Home Building',
  'Industrial',
  'Linear/Utility',
  'Roadway/Transportation',
  'Commercial',
  'State',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\(\d{3}\) \d{3}-\d{4}$/;

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

type BillingState = {
  billing_customer_name: string;
  billing_co: string;
  billing_address: string;
  billing_email_1: string;
  billing_email_2: string;
  billing_email_3: string;
  billing_email_4: string;
  billing_phone_1_number: string;
  billing_phone_1_name: string;
  billing_phone_1_title: string;
  billing_phone_2_number: string;
  billing_phone_2_name: string;
  billing_phone_2_title: string;
  billing_phone_3_number: string;
  billing_phone_3_name: string;
  billing_phone_3_title: string;
  billing_additional_info: string;
  billing_project_type: string;
};

function validateBilling(b: BillingState): Partial<Record<keyof BillingState, string>> {
  const errors: Partial<Record<keyof BillingState, string>> = {};
  if (!b.billing_customer_name.trim()) errors.billing_customer_name = 'Required';
  if (!b.billing_address.trim()) errors.billing_address = 'Required';
  if (!b.billing_email_1.trim()) {
    errors.billing_email_1 = 'Required';
  } else if (!EMAIL_RE.test(b.billing_email_1.trim())) {
    errors.billing_email_1 = 'Invalid email address';
  }
  if (b.billing_email_2.trim() && !EMAIL_RE.test(b.billing_email_2.trim())) errors.billing_email_2 = 'Invalid email address';
  if (b.billing_email_3.trim() && !EMAIL_RE.test(b.billing_email_3.trim())) errors.billing_email_3 = 'Invalid email address';
  if (b.billing_email_4.trim() && !EMAIL_RE.test(b.billing_email_4.trim())) errors.billing_email_4 = 'Invalid email address';
  if (!b.billing_phone_1_number.trim()) {
    errors.billing_phone_1_number = 'Required';
  } else if (!PHONE_RE.test(b.billing_phone_1_number.trim())) {
    errors.billing_phone_1_number = 'Must be (XXX) XXX-XXXX';
  }
  if (!b.billing_phone_1_name.trim()) errors.billing_phone_1_name = 'Required';
  if (!b.billing_phone_1_title.trim()) errors.billing_phone_1_title = 'Required';
  if (b.billing_phone_2_number.trim() && !PHONE_RE.test(b.billing_phone_2_number.trim())) errors.billing_phone_2_number = 'Must be (XXX) XXX-XXXX';
  if (b.billing_phone_3_number.trim() && !PHONE_RE.test(b.billing_phone_3_number.trim())) errors.billing_phone_3_number = 'Must be (XXX) XXX-XXXX';
  if (!b.billing_project_type) errors.billing_project_type = 'Required';
  return errors;
}

const inputStyle = (hasError = false): React.CSSProperties => ({
  width: '100%',
  border: `1px solid ${hasError ? '#c00' : '#ccc'}`,
  borderRadius: 3,
  padding: '5px 8px',
  fontSize: 13,
  boxSizing: 'border-box',
});

const fieldLabel = (text: string, required = false): React.ReactNode => (
  <label style={{ display: 'block', fontSize: 12, color: '#444', marginBottom: 3, fontWeight: 600 }}>
    {text}{required && <span style={{ color: '#c00', marginLeft: 2 }}>*</span>}
  </label>
);

const FieldError = ({ msg }: { msg?: string }) =>
  msg ? <div style={{ color: '#c00', fontSize: 11, marginTop: 2 }}>{msg}</div> : null;

const AuthorizationAgreementModal: React.FC<Props> = ({
  show,
  onClose,
  projectId,
  agreement,
  signability,
  onSigned,
  clientName,
}) => {
  const appStore = useContext(AppContext);
  const user = appStore.get<any>('user');
  const currentUserName = user
    ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
    : '';
  const currentUserTitle = user?.position ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingErrors, setBillingErrors] = useState<Partial<Record<keyof BillingState, string>>>({});
  const [touched, setTouched] = useState(false);

  const [billing, setBilling] = useState({
    billing_customer_name: agreement?.billing_customer_name ?? '',
    billing_co: agreement?.billing_co ?? '',
    billing_address: agreement?.billing_address ?? '',
    billing_email_1: agreement?.billing_email_1 ?? '',
    billing_email_2: agreement?.billing_email_2 ?? '',
    billing_email_3: agreement?.billing_email_3 ?? '',
    billing_email_4: agreement?.billing_email_4 ?? '',
    billing_phone_1_number: agreement?.billing_phone_1_number ?? '',
    billing_phone_1_name: agreement?.billing_phone_1_name ?? '',
    billing_phone_1_title: agreement?.billing_phone_1_title ?? '',
    billing_phone_2_number: agreement?.billing_phone_2_number ?? '',
    billing_phone_2_name: agreement?.billing_phone_2_name ?? '',
    billing_phone_2_title: agreement?.billing_phone_2_title ?? '',
    billing_phone_3_number: agreement?.billing_phone_3_number ?? '',
    billing_phone_3_name: agreement?.billing_phone_3_name ?? '',
    billing_phone_3_title: agreement?.billing_phone_3_title ?? '',
    billing_additional_info: agreement?.billing_additional_info ?? '',
    billing_project_type: agreement?.billing_project_type ?? '',
  });

  useEffect(() => {
    if (agreement) {
      setBilling({
        billing_customer_name: agreement.billing_customer_name ?? '',
        billing_co: agreement.billing_co ?? '',
        billing_address: agreement.billing_address ?? '',
        billing_email_1: agreement.billing_email_1 ?? '',
        billing_email_2: agreement.billing_email_2 ?? '',
        billing_email_3: agreement.billing_email_3 ?? '',
        billing_email_4: agreement.billing_email_4 ?? '',
        billing_phone_1_number: agreement.billing_phone_1_number ?? '',
        billing_phone_1_name: agreement.billing_phone_1_name ?? '',
        billing_phone_1_title: agreement.billing_phone_1_title ?? '',
        billing_phone_2_number: agreement.billing_phone_2_number ?? '',
        billing_phone_2_name: agreement.billing_phone_2_name ?? '',
        billing_phone_2_title: agreement.billing_phone_2_title ?? '',
        billing_phone_3_number: agreement.billing_phone_3_number ?? '',
        billing_phone_3_name: agreement.billing_phone_3_name ?? '',
        billing_phone_3_title: agreement.billing_phone_3_title ?? '',
        billing_additional_info: agreement.billing_additional_info ?? '',
        billing_project_type: agreement.billing_project_type ?? '',
      });
    }
  }, [agreement?.id]);

  const set = useCallback((field: keyof BillingState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const val = e.target.value;
    setBilling((prev) => {
      const next = { ...prev, [field]: val };
      if (touched) setBillingErrors(validateBilling(next));
      return next;
    });
  }, [touched]);

  const setPhone = useCallback((field: keyof BillingState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = formatPhone(e.target.value);
    setBilling((prev) => {
      const next = { ...prev, [field]: val };
      if (touched) setBillingErrors(validateBilling(next));
      return next;
    });
  }, [touched]);

  const handleSaveBilling = async () => {
    setTouched(true);
    const errors = validateBilling(billing);
    setBillingErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the billing form before saving');
      return;
    }
    setSavingBilling(true);
    try {
      await authorizationAgreementApi.saveBilling(projectId, billing);
      onSigned();
      toast.success('Billing information saved');
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error saving billing information');
    } finally {
      setSavingBilling(false);
    }
  };

  const isSigned = !!(agreement?.client_signed_at && agreement?.inspector_signed_at);
  const billingReadOnly = isSigned;

  const handleSign = async (block: 'client' | 'inspector') => {
    setSubmitting(true);
    try {
      await authorizationAgreementApi.signBlock(projectId, block);
      toast.success(`${block === 'client' ? 'Client' : 'Inspector'} signature saved`);
      onSigned();
    } catch (err) {
      const error = err as any;
      toast.error(error?.response?.data?.error || 'Error saving signature');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      title='Authorization and Agreement for Storm-Water Inspections'
      handleClose={onClose}
      handleSubmit={onClose}
      submitBtnText='Close'
      cancelBtn={false}
    >
      <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 4px', fontSize: 13, lineHeight: 1.6 }}>
        <p>
          This Authorization and Agreement ("Agreement") is made by and between{' '}
          <strong>{clientName ?? '[Account/Client Name]'}</strong> ("Client") and{' '}
          <strong>CMS Environmental Solutions, LLC</strong> ("Inspector"), effective as of{' '}
          {agreement?.client_signed_at
            ? new Date(agreement.client_signed_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            : '[Date]'}.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>1. Authorization</h4>
        <p>
          Client hereby authorizes Inspector to perform storm-water inspection services (the
          "Services") on Client's behalf in accordance with all applicable laws, regulations,
          and standards. Client will provide all necessary licenses, permits, rights of access
          or other rights necessary for Inspector to perform the Services.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>2. Term; Termination</h4>
        <p>
          This Agreement shall remain in full force and effect unless and until terminated by
          Client through written notice delivered to Inspector, which shall be effective upon
          receipt by Inspector.
        </p>
        <p>
          If the Client requests that inspections cease, such request must be submitted in
          writing to the assigned Inspector or Area Manager and include{' '}
          <a href='mailto:CMSOperations@cmsenviro.com'>CMSOperations@cmsenviro.com</a>.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>3. Compensation and Payment</h4>
        <p>
          Billing Department shall issue invoices to Client for Services rendered. Client
          agrees to remit payment in full within 60 days of the invoice date.
        </p>
        <p>
          If Client fails to make timely payment, Inspector reserves the right to suspend or
          terminate the Services until all outstanding balances are paid in full.
        </p>
        <p>
          Invoices shall be deemed received once transmitted to the email address(es) provided
          in the Billing Information supplement, attached to this Agreement as Exhibit A.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>4. Liability</h4>
        <p>
          Inspector shall perform services with reasonable care and in a professional manner
          consistent with industry standards. Inspector shall not be liable for any damages,
          penalties, or losses arising from conditions outside its control, including but not
          limited to natural events, acts of third parties, or Client's failure to comply with
          applicable requirements. Inspector's maximum liability to Client shall not exceed the
          amounts payable under this Agreement. INSPECTOR SHALL HAVE NO LIABILITY, WHETHER
          ARISING IN CONTRACT (INCLUDING WARRANTY), TORT (INCLUDING NEGLIGENCE, STRICT LIABILITY
          OR PRODUCT LIABILITY) OR OTHERWISE FOR ANY PUNITIVE, EXEMPLARY, SPECIAL, INCIDENTAL,
          CONSEQUENTIAL OR INDIRECT DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF USE, BUSINESS
          INTERRUPTION, LOSS OF REVENUE, LOSS OF BUSINESS OR OTHER FINANCIAL LOSS ARISING OUT
          OF OR IN CONNECTION WITH THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>5. Indemnification</h4>
        <p>
          Client agrees to indemnify, defend, and hold harmless Inspector, its officers,
          employees, and agents from and against any and all claims, liabilities, damages,
          losses, and expenses (including reasonable attorneys' fees) arising out of or related
          to this Agreement, except to the extent such claim arises solely from the gross
          negligence or willful misconduct of Inspector.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>6. Governing Law and Jurisdiction</h4>
        <p>
          This Agreement shall be governed by and construed in accordance with the laws of the
          State of [Insert State], without regard to any conflict of laws principles. Any
          disputes arising under or relating to this Agreement shall be subject to the exclusive
          jurisdiction of the state and federal courts located in [Insert County, State], and
          the parties hereby consent to the personal jurisdiction of such courts.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>7. Severability</h4>
        <p>
          If any provision of this Agreement is determined to be invalid, illegal, or
          unenforceable, the remaining provisions shall remain in full force and effect.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>8. Entire Agreement</h4>
        <p>
          This Agreement constitutes the entire understanding between the parties with respect
          to the subject matter hereof and supersedes all prior or contemporaneous oral or
          written agreements, representations, or communications. Any amendments or
          modifications must be in writing and signed by both parties.
        </p>

        <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>9. Binding Effect</h4>
        <p>
          This Agreement constitutes a binding contract between the parties and shall remain
          enforceable according to its terms.
        </p>

        {/* ── Exhibit A ── */}
        <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />
        <h4 style={{ margin: '0 0 4px', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Exhibit A — Billing Information
        </h4>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#666', fontStyle: 'italic' }}>
          Where are invoices being sent to
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 10 }}>
          <div>
            {fieldLabel('Billing Name', true)}
            <input style={inputStyle(!!billingErrors.billing_customer_name)} value={billing.billing_customer_name} onChange={set('billing_customer_name')} disabled={billingReadOnly} placeholder='Customer Name' />
            <FieldError msg={billingErrors.billing_customer_name} />
          </div>
          <div>
            {fieldLabel('c/o (if applicable)')}
            <input style={inputStyle()} value={billing.billing_co} onChange={set('billing_co')} disabled={billingReadOnly} placeholder='c/o' />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Billing Address', true)}
          <input style={inputStyle(!!billingErrors.billing_address)} value={billing.billing_address} onChange={set('billing_address')} disabled={billingReadOnly} placeholder='Street, City, State, Zip Code' />
          <FieldError msg={billingErrors.billing_address} />
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Email(s) for Invoices', true)}
          <input style={{ ...inputStyle(!!billingErrors.billing_email_1), marginBottom: 2 }} value={billing.billing_email_1} onChange={set('billing_email_1')} disabled={billingReadOnly} placeholder='Accounts payable email (required)' />
          <FieldError msg={billingErrors.billing_email_1} />
          <div style={{ marginTop: 4 }}>
            <input style={{ ...inputStyle(!!billingErrors.billing_email_2), marginBottom: 2 }} value={billing.billing_email_2} onChange={set('billing_email_2')} disabled={billingReadOnly} placeholder='Second email (optional)' />
            <FieldError msg={billingErrors.billing_email_2} />
          </div>
          <div style={{ marginTop: 4 }}>
            <input style={{ ...inputStyle(!!billingErrors.billing_email_3), marginBottom: 2 }} value={billing.billing_email_3} onChange={set('billing_email_3')} disabled={billingReadOnly} placeholder='Third email (optional)' />
            <FieldError msg={billingErrors.billing_email_3} />
          </div>
          <div style={{ marginTop: 4 }}>
            <input style={inputStyle(!!billingErrors.billing_email_4)} value={billing.billing_email_4} onChange={set('billing_email_4')} disabled={billingReadOnly} placeholder='Fourth email (optional)' />
            <FieldError msg={billingErrors.billing_email_4} />
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Phone Number(s)', true)}
          {([
            ['billing_phone_1_number', 'billing_phone_1_name', 'billing_phone_1_title', true],
            ['billing_phone_2_number', 'billing_phone_2_name', 'billing_phone_2_title', false],
            ['billing_phone_3_number', 'billing_phone_3_name', 'billing_phone_3_title', false],
          ] as [keyof BillingState, keyof BillingState, keyof BillingState, boolean][]).map(([numF, nameF, titleF, isFirst], i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              {isFirst && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 4px', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: '#444' }}>Phone<span style={{ color: '#c00' }}>*</span></span>
                  <span style={{ fontSize: 11, color: '#444' }}>Name<span style={{ color: '#c00' }}>*</span></span>
                  <span style={{ fontSize: 11, color: '#444' }}>Title<span style={{ color: '#c00' }}>*</span></span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                <div>
                  <input style={inputStyle(!!billingErrors[numF])} value={billing[numF]} onChange={setPhone(numF)} disabled={billingReadOnly} placeholder='(XXX) XXX-XXXX' />
                  <FieldError msg={billingErrors[numF]} />
                </div>
                <div>
                  <input style={inputStyle(!!billingErrors[nameF])} value={billing[nameF]} onChange={set(nameF)} disabled={billingReadOnly} placeholder='Name' />
                  <FieldError msg={billingErrors[nameF]} />
                </div>
                <div>
                  <input style={inputStyle(!!billingErrors[titleF])} value={billing[titleF]} onChange={set(titleF)} disabled={billingReadOnly} placeholder='Title' />
                  <FieldError msg={billingErrors[titleF]} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Additional Billing Information')}
          <textarea style={{ ...inputStyle(), minHeight: 60, resize: 'vertical' } as React.CSSProperties} value={billing.billing_additional_info} onChange={set('billing_additional_info')} disabled={billingReadOnly} />
        </div>

        <div style={{ marginBottom: 10 }}>
          {fieldLabel('Project Type', true)}
          <select style={inputStyle(!!billingErrors.billing_project_type)} value={billing.billing_project_type} onChange={set('billing_project_type')} disabled={billingReadOnly}>
            <option value=''>— Select —</option>
            {PROJECT_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          <FieldError msg={billingErrors.billing_project_type} />
        </div>

        {!billingReadOnly && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <button className='primary small' onClick={handleSaveBilling} disabled={savingBilling}>
              {savingBilling ? 'Saving…' : 'Save Billing Info'}
            </button>
          </div>
        )}

        <hr style={{ margin: '20px 0', borderColor: '#ddd' }} />

        <p style={{ textAlign: 'center', fontStyle: 'italic', color: '#555' }}>
          [Signature Page Follows]
        </p>
        <p>
          In witness whereof, the parties have executed this Agreement effective as of the date
          last set forth below.
        </p>

        <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
          <SignatureBlock
            role='Client'
            name={agreement?.client_name ?? null}
            title={agreement?.client_title ?? null}
            signedAt={agreement?.client_signed_at ?? null}
            canSign={signability.canSignClient && !agreement?.client_signed_at}
            currentUserName={currentUserName}
            currentUserTitle={currentUserTitle}
            onSign={() => handleSign('client')}
            disabled={submitting}
          />
          <SignatureBlock
            role='Inspector'
            name={agreement?.inspector_name ?? null}
            title={agreement?.inspector_title ?? null}
            signedAt={agreement?.inspector_signed_at ?? null}
            canSign={signability.canSignInspector && !agreement?.inspector_signed_at}
            currentUserName={currentUserName}
            currentUserTitle={currentUserTitle}
            onSign={() => handleSign('inspector')}
            disabled={submitting}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AuthorizationAgreementModal;
